import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { CloudUpload, Save } from '@mui/icons-material';
import { NumericTextField } from '../../../components/forms/NumericTextField';
import type { RockTCSGeneral, RockTCSReadingsPayload, RockTCSStageMeta } from '../../../types/rockTcs';
import { getRockTcsReadings, uploadRockTcsCsv } from '../../../services/rockTcsService';
import { PEAK_RESULT_ROWS } from './rockTcsFields';
import { ReadingsDataPanel } from './sections/ReadingsDataPanel';
import { FailureModeSection } from './sections/FailureModeSection';
import { DerivedValue, fmt, inputSx, paperSx, SectionHeader, unit } from './sectionStyles';

interface Props {
  workOrderDetailKey: string;
  general: RockTCSGeneral;
  stageMeta: RockTCSStageMeta[];
  readingCount: number;
  onChange: (field: keyof RockTCSGeneral, value: number | string | null) => void;
  onReloadRequested: () => void;
  onSave: () => void;
  onNotify: (message: string, severity: 'success' | 'error') => void;
  hasUnsavedParameters: boolean;
  readOnly?: boolean;
}

/**
 * Result Entry tab (mockups: Rock TCS result Entry 1/2.png):
 * 1. Machine Data Upload (GCTS CSV)  2. Peak Test Results (auto + overrides)
 * 3. Result Curve Preview  4. Combined Test Data & Calculations | Failure
 * Mode & Observations sub-tabs.
 */
export const RockTCSResultEntryTab = ({
  workOrderDetailKey, general, stageMeta, readingCount, onChange,
  onReloadRequested, onSave, onNotify, hasUnsavedParameters, readOnly,
}: Props) => {
  const [readingsPayload, setReadingsPayload] = useState<RockTCSReadingsPayload | null>(null);
  const [loadingReadings, setLoadingReadings] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadWarning, setUploadWarning] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [subTab, setSubTab] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Readings exist but nothing is computed → the CSV was uploaded before the
  // specimen parameters were saved (no area / gauge length to calculate with).
  const hasUncomputedData =
    (general.sourceDataPoints ?? 0) > 0 && general.computedTCSMPa == null;

  const loadReadings = useCallback(async () => {
    if (readingCount === 0) { setReadingsPayload(null); return; }
    setLoadingReadings(true);
    try {
      setReadingsPayload(await getRockTcsReadings(workOrderDetailKey));
    } catch {
      onNotify('Failed to load readings', 'error');
    } finally {
      setLoadingReadings(false);
    }
  }, [workOrderDetailKey, readingCount, onNotify]);

  useEffect(() => { void loadReadings(); }, [loadReadings]);

  const handleFile = useCallback(async (file: File) => {
    setUploading(true);
    setUploadError(null);
    setUploadWarning(null);
    try {
      const result = await uploadRockTcsCsv(workOrderDetailKey, file);
      onNotify(
        `Uploaded ${result.fileName}: ${result.dataPoints.toLocaleString()} points from stage ${result.staticStageIndex} (Static Loading)`,
        'success',
      );
      if (!result.computed) {
        setUploadWarning(
          'Raw data stored, but nothing was calculated yet — the specimen parameters are missing. ' +
          'Enter the 6 diameters and 3 gauge lengths on the Test Parameters tab and press Save; ' +
          'all stresses, strains, moduli and the summary will compute automatically.',
        );
      }
      if (result.warnings.length > 0) onNotify(result.warnings.join(' '), 'error');
      onReloadRequested();
    } catch (err) {
      const resp = (err as { response?: { data?: { error?: string; message?: string } } })?.response;
      setUploadError(resp?.data?.message || 'Upload failed — is this a GCTS CATS CSV export?');
    } finally {
      setUploading(false);
    }
  }, [workOrderDetailKey, onNotify, onReloadRequested]);

  const staticMeta = stageMeta.find((s) => s.isStaticLoading);

  // ── Result curve preview (axial stress vs axial + radial strain) ──
  const previewOptions = useMemo((): Highcharts.Options | null => {
    if (!readingsPayload || readingsPayload.readings.length === 0) return null;
    const end = (readingsPayload.chartEndIndex ?? readingsPayload.readings.length - 1) + 1;
    const rows = readingsPayload.readings.slice(0, end);
    const axial = rows
      .filter((r) => r.computedAvgAxialStrainUE !== null && r.computedAxialStressMPa !== null)
      .map((r) => [r.computedAvgAxialStrainUE as number, r.computedAxialStressMPa as number]);
    const radial = rows
      .filter((r) => r.computedRadialStrainUE !== null && r.computedAxialStressMPa !== null)
      .map((r) => [r.computedRadialStrainUE as number, r.computedAxialStressMPa as number]);
    return {
      chart: { height: 260, animation: false, spacing: [8, 8, 4, 4] },
      title: { text: undefined },
      credits: { enabled: false },
      legend: { enabled: true, itemStyle: { fontSize: '0.65rem' } },
      xAxis: {
        title: { text: 'Microstrain (µε)', style: { fontSize: '0.65rem' } },
        reversed: true,
        labels: { style: { fontSize: '0.6rem' } },
      },
      yAxis: {
        title: { text: 'Axial Stress (MPa)', style: { fontSize: '0.65rem' } },
        labels: { style: { fontSize: '0.6rem' } },
      },
      tooltip: { valueDecimals: 1 },
      plotOptions: { series: { animation: false, marker: { enabled: false } } },
      series: [
        { type: 'line', name: 'Axial Strain', data: axial, color: '#3E4A3E' },
        { type: 'line', name: 'Radial Strain', data: radial, color: '#3B5B7E', dashStyle: 'Dash' },
      ],
    };
  }, [readingsPayload]);

  return (
    <Box sx={{ display: 'grid', rowGap: 1.5, pb: 1 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr 1.1fr', gap: 1.5 }}>
        {/* ── 1. Machine data upload ── */}
        <Paper variant="outlined" sx={paperSx}>
          <SectionHeader label="1. Machine Data Upload" />
          {hasUnsavedParameters && (
            <Alert severity="warning" sx={{ mb: 1, py: 0, fontSize: '0.72rem' }}>
              Unsaved parameters — save first so strain calcs use the latest gauge length.
            </Alert>
          )}
          <Box
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              if (!readOnly && e.dataTransfer.files[0]) void handleFile(e.dataTransfer.files[0]);
            }}
            sx={{
              border: '2px dashed',
              borderColor: dragOver ? 'primary.main' : 'divider',
              borderRadius: 1,
              p: 2,
              textAlign: 'center',
              backgroundColor: dragOver ? 'rgba(74, 103, 65, 0.06)' : 'transparent',
              mb: 1,
            }}
          >
            <CloudUpload sx={{ fontSize: 30, color: 'text.disabled' }} />
            <Typography variant="body2" sx={{ fontSize: '0.76rem', mb: 0.5 }}>
              Drag & drop the GCTS CSV here
            </Typography>
            <Button
              size="small" variant="outlined" disabled={readOnly || uploading}
              onClick={() => fileInputRef.current?.click()}
              sx={{ textTransform: 'none' }}
            >
              {uploading ? <CircularProgress size={16} /> : 'Choose File'}
            </Button>
            <input
              ref={fileInputRef} type="file" accept=".csv,text/csv" hidden
              onChange={(e) => { if (e.target.files?.[0]) void handleFile(e.target.files[0]); e.target.value = ''; }}
            />
          </Box>
          {uploadError && <Alert severity="error" sx={{ py: 0, fontSize: '0.72rem', mb: 1 }}>{uploadError}</Alert>}
          {(uploadWarning || hasUncomputedData) && (
            <Alert severity="warning" sx={{ py: 0, fontSize: '0.72rem', mb: 1 }}>
              {uploadWarning ??
                'Data is uploaded but not calculated — specimen parameters (diameters, gauge lengths) were not saved before the upload. Fill them in on Test Parameters and press Save to compute everything.'}
            </Alert>
          )}

          {general.sourceFileName ? (
            <Box sx={{ display: 'grid', rowGap: 0.4 }}>
              <DerivedValue label="File" value={general.sourceFileName} />
              <DerivedValue label="Uploaded by" value={general.sourceUploadedBy ?? '—'} />
              <DerivedValue label="Uploaded on" value={general.sourceUploadedDate ? String(general.sourceUploadedDate).slice(0, 10) : '—'} />
              <DerivedValue label="Stages in file" value={String(general.sourceStageCount ?? '—')} />
              <DerivedValue label="Static stage used" value={String(general.sourceStaticStageIndex ?? '—')} />
              <DerivedValue label="Data points" value={(general.sourceDataPoints ?? 0).toLocaleString()} />
            </Box>
          ) : (
            <Typography variant="caption" color="text.disabled">No machine data uploaded yet.</Typography>
          )}
        </Paper>

        {/* ── 2. Peak test results ── */}
        <Paper variant="outlined" sx={paperSx}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <SectionHeader label="2. Peak Test Results (auto-selected at 50% of TCS)" />
            {!readOnly && (
              <Button size="small" startIcon={<Save sx={{ fontSize: 14 }} />} onClick={onSave} sx={{ textTransform: 'none', fontSize: '0.7rem' }}>
                Save overrides
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', columnGap: 2, rowGap: 0.6 }}>
            {PEAK_RESULT_ROWS.map((row) => {
              const auto = general[row.computedField] as number | null | undefined;
              // BUG-2026-0137 §5 — Test Duration shows total seconds AND
              // decimal minutes (seconds / 60), both visible.
              const minutesSuffix = row.showMinutes && auto != null ? ` (${(auto / 60).toFixed(2)} min)` : '';
              return (
                <Box key={row.label} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.72rem', flex: 1 }}>{row.label}</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.74rem', fontWeight: 600, minWidth: 64, textAlign: 'right' }}>
                    {fmt(auto ?? null, row.decimals)}{row.unit ? ` ${row.unit}` : ''}{minutesSuffix}
                  </Typography>
                  {row.overrideField ? (
                    <NumericTextField
                      size="small"
                      placeholder="override"
                      value={general[row.overrideField] as number | null}
                      onChange={(v) => onChange(row.overrideField as keyof RockTCSGeneral, v)}
                      InputProps={{ readOnly, endAdornment: row.unit ? unit(row.unit) : undefined }}
                      sx={{ ...inputSx, width: 110 }}
                    />
                  ) : (
                    <Box sx={{ width: 110 }} />
                  )}
                </Box>
              );
            })}
          </Box>
          {staticMeta && (
            <Box sx={{ mt: 1, pt: 0.75, borderTop: '1px dashed', borderColor: 'divider', display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.66rem' }}>
                GCTS machine cross-check:
              </Typography>
              <Chip size="small" sx={{ height: 18, fontSize: '0.64rem' }} label={`Peak Sd ${fmt(staticMeta.machinePeakSdMPa, 1)} MPa`} />
              <Chip size="small" sx={{ height: 18, fontSize: '0.64rem' }} label={`E @50% ${fmt(staticMeta.machine50YoungsModulusGPa, 2)} GPa`} />
              <Chip size="small" sx={{ height: 18, fontSize: '0.64rem' }} label={`ν @50% ${fmt(staticMeta.machine50PoissonsRatio, 2)}`} />
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.62rem' }}>
                (advisory — machine uses its own strain channels)
              </Typography>
            </Box>
          )}
        </Paper>

        {/* ── 3. Result curve preview ── */}
        <Paper variant="outlined" sx={paperSx}>
          <SectionHeader label="3. Result Curve Preview" />
          {loadingReadings ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={22} /></Box>
          ) : previewOptions ? (
            <HighchartsReact highcharts={Highcharts} options={previewOptions} />
          ) : (
            <Typography variant="caption" color="text.disabled">Upload machine data to see the curve.</Typography>
          )}
        </Paper>
      </Box>

      {/* ── 4. Data | Failure mode sub-tabs ── */}
      <Box>
        <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} sx={{ minHeight: 36, mb: 1, '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontSize: '0.78rem' } }}>
          <Tab label={`Test Data & Calculations${readingsPayload ? ` (${readingsPayload.readings.length.toLocaleString()} rows)` : ''}`} />
          <Tab label="Failure Mode & Observations" />
        </Tabs>
        {subTab === 0 && (
          readingsPayload && readingsPayload.readings.length > 0 ? (
            <ReadingsDataPanel
              workOrderDetailKey={workOrderDetailKey}
              readings={readingsPayload.readings}
              maxRowIndex={readingsPayload.maxRowIndex}
              fiftyRowIndex={readingsPayload.fiftyRowIndex}
              readOnly={readOnly}
              onSaved={() => { void loadReadings(); onReloadRequested(); }}
              onNotify={onNotify}
            />
          ) : (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No imported data yet — upload the GCTS CSV above.
              </Typography>
            </Paper>
          )
        )}
        {subTab === 1 && (
          <FailureModeSection general={general} onChange={onChange} readOnly={readOnly} workOrderDetailKey={workOrderDetailKey} />
        )}
      </Box>
    </Box>
  );
};
